import React from "react";
import { NavLink, useNavigate } from "react-router-dom";

// MUI
import { Call, Instagram, WhatsApp, YouTube, LocationOn, AdminPanelSettings, Store, Person, Home, School, VerifiedUser, Collections, ContactMail } from "@mui/icons-material";
import { Box, Button, Container, Stack, Typography, useTheme, Grid, Divider } from "@mui/material";

export default function Footer() {
	const navi = useNavigate();
	const theme = useTheme();

	return (
		<Box
			sx={{
				background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
				position: "relative",
				overflow: "hidden",
				"&::before": {
					content: '""',
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundImage: `radial-gradient(${theme.palette.secondary.main}15 1px, transparent 1px)`,
					backgroundSize: "30px 30px",
					opacity: 0.3,
				}
			}}
			pt={8}
			pb={3}
		>
			<Container sx={{ position: "relative", zIndex: 1 }}>
				{/* Main Footer Content */}
				<Grid container spacing={4} pb={4}>
					{/* Site Map */}
					<Grid item xs={12} md={3}>
						<Typography
							fontSize={16}
							color={theme.palette.secondary.main}
							fontWeight={700}
							letterSpacing={2}
							mb={2}
						>
							SITE MAP
						</Typography>
						<Stack spacing={1.5}>
							{[
								{ name: "Home", link: "/", icon: Home },
								{ name: "Courses", link: "/courses", icon: School },
								{ name: "Verification", link: "/verification", icon: VerifiedUser },
								{ name: "Gallery", link: "/gallery", icon: Collections },
								{ name: "Contact", link: "/contact", icon: ContactMail },
							].map((item, index) => {
								const Icon = item.icon;
								return (
									<Box
										key={index}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											transition: "all 0.3s ease",
											"&:hover": {
												"& svg": { color: theme.palette.secondary.main },
												"& a": { color: theme.palette.secondary.main }
											}
										}}
									>
										<Icon sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: 20, transition: "color 0.3s ease" }} />
										<NavLink
											style={{
												color: "rgba(255, 255, 255, 0.8)",
												textDecoration: "none",
												transition: "color 0.3s ease"
											}}
											to={item.link}
										>
											{item.name}
										</NavLink>
									</Box>
								);
							})}
						</Stack>
					</Grid>

					{/* Login Section */}
					<Grid item xs={12} md={3}>
						<Typography
							fontSize={16}
							color={theme.palette.secondary.main}
							fontWeight={700}
							letterSpacing={2}
							mb={2}
						>
							LOGIN
						</Typography>
						<Stack spacing={1.5}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									transition: "all 0.3s ease",
									"&:hover": {
										"& svg": { color: theme.palette.secondary.main },
										"& a": { color: theme.palette.secondary.main }
									}
								}}
							>
								<AdminPanelSettings sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: 20, transition: "color 0.3s ease" }} />
								<NavLink
									style={{
										color: "rgba(255, 255, 255, 0.8)",
										textDecoration: "none",
										transition: "color 0.3s ease"
									}}
									to="/login-admin"
								>
									Admin Login
								</NavLink>
							</Box>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									transition: "all 0.3s ease",
									"&:hover": {
										"& svg": { color: theme.palette.secondary.main },
										"& a": { color: theme.palette.secondary.main }
									}
								}}
							>
								<Store sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: 20, transition: "color 0.3s ease" }} />
								<NavLink
									style={{
										color: "rgba(255, 255, 255, 0.8)",
										textDecoration: "none",
										transition: "color 0.3s ease"
									}}
									to="/login-franchise"
								>
									Franchise Login
								</NavLink>
							</Box>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									transition: "all 0.3s ease",
									"&:hover": {
										"& svg": { color: theme.palette.secondary.main },
										"& a": { color: theme.palette.secondary.main }
									}
								}}
							>
								<Person sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: 20, transition: "color 0.3s ease" }} />
								<NavLink
									style={{
										color: "rgba(255, 255, 255, 0.8)",
										textDecoration: "none",
										transition: "color 0.3s ease"
									}}
									to="/student/home"
								>
									Student Login
								</NavLink>
							</Box>
						</Stack>
					</Grid>

					{/* Contact Info */}
					<Grid item xs={12} md={3}>
						<Typography
							fontSize={16}
							color={theme.palette.secondary.main}
							fontWeight={700}
							letterSpacing={2}
							mb={2}
						>
							CONTACT INFO
						</Typography>
						<Stack spacing={2}>
							<Box sx={{ display: "flex", gap: 1, alignItems: "start" }}>
								<LocationOn sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
								<Typography color="rgba(255, 255, 255, 0.9)" fontSize={14}>
									Mirchaibari road, Katihar - 854105<br />
									Bihar, India
								</Typography>
							</Box>
							<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
								<Call sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
								<Typography color="rgba(255, 255, 255, 0.9)" fontSize={14}>
									+91 7808426759
								</Typography>
							</Box>
						</Stack>
					</Grid>

					{/* Social & CTA */}
					<Grid item xs={12} md={3}>
						<Typography
							fontSize={16}
							color={theme.palette.secondary.main}
							fontWeight={700}
							letterSpacing={2}
							mb={2}
						>
							CONNECT WITH US
						</Typography>
						<Stack spacing={1.5}>
							<Button
								variant="outlined"
								startIcon={<YouTube />}
								onClick={() => window.location.href = "https://youtube.com/@spywarecomputers?si=Yk4z9n4dNQu0MCcN"}
								sx={{
									borderColor: "rgba(255, 255, 255, 0.3)",
									color: "white",
									"&:hover": {
										borderColor: theme.palette.secondary.main,
										background: `${theme.palette.secondary.main}20`
									}
								}}
								size="small"
							>
								YouTube
							</Button>
							<Button
								variant="outlined"
								startIcon={<Instagram />}
								onClick={() => window.location.href = "https://www.instagram.com/spywarecomputers/"}
								sx={{
									borderColor: "rgba(255, 255, 255, 0.3)",
									color: "white",
									"&:hover": {
										borderColor: theme.palette.secondary.main,
										background: `${theme.palette.secondary.main}20`
									}
								}}
								size="small"
							>
								Instagram
							</Button>
							<Button
								variant="outlined"
								startIcon={<WhatsApp />}
								onClick={() => window.location.href = "https://wa.me/917808426759"}
								sx={{
									borderColor: "rgba(255, 255, 255, 0.3)",
									color: "white",
									"&:hover": {
										borderColor: theme.palette.secondary.main,
										background: `${theme.palette.secondary.main}20`
									}
								}}
								size="small"
							>
								WhatsApp
							</Button>
						</Stack>

						<Button
							variant="contained"
							color="secondary"
							fullWidth
							sx={{
								mt: 3,
								fontWeight: 700,
								color: theme.palette.primary.main,
								boxShadow: `0 4px 20px ${theme.palette.secondary.main}40`,
								"&:hover": {
									boxShadow: `0 6px 30px ${theme.palette.secondary.main}60`,
								}
							}}
							onClick={() => navi(`/admission`)}
						>
							Join Spyware
						</Button>
					</Grid>
				</Grid>

				<Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mb: 3 }} />

				{/* Bottom Bar */}
				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ md: "center", xs: "start" }}
					justifyContent="space-between"
					spacing={2}
				>
					<Typography color="rgba(255, 255, 255, 0.7)" fontSize={13}>
						© {new Date().getFullYear()} Spyware Computers. All rights reserved. (
						<NavLink
							style={{
								color: theme.palette.secondary.main,
								fontSize: 13,
								textDecoration: "underline"
							}}
							to="/terms&conditions"
						>
							Terms & Conditions
						</NavLink>
						)
					</Typography>
					<a href="https://passionfrom.com" target="_blank" rel="noopener noreferrer">
						<img
							src="https://passionfrom.com/passionfromassets/whitePassionfrom.png"
							style={{ width: 100, opacity: 0.8 }}
							alt="Passionfrom"
						/>
					</a>
				</Stack>
			</Container>
		</Box>
	);
}
